import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { movies, categories, faqs } from '@/mocks/movies';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="w-3 h-3 flex items-center justify-center">
          <i className={`text-xs ${i < Math.round(rating) ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-400'}`}></i>
        </span>
      ))}
    </div>
  );
}

function MovieCardFeatured({ id, title, description, rating, poster, year, categoryName }: {
  id: string; title: string; description: string; rating: number; poster: string; year: number; categoryName?: string;
}) {
  return (
    <Link to={`/movie/${id}`} className="group block cursor-pointer min-w-[280px] sm:min-w-[320px] snap-start" data-product-shop="">
      <div className="relative overflow-hidden rounded-lg aspect-[16/9] bg-background-200 mb-3">
        <img
          src={poster}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          title={`${title} - 神马影院高清在线观看`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-primary-500 text-background-50 text-xs font-bold px-2 py-0.5 rounded">{rating}</span>
            <span className="text-xs text-white/80">{year}</span>
            {categoryName && <span className="text-xs text-white/60">{categoryName}</span>}
          </div>
          <h3 className="text-base font-bold text-white truncate">{title}</h3>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-primary-500/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <i className="ri-play-fill text-white text-2xl"></i>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const featuredMovies = movies.filter(m => m.featured);
  const latestMovies = movies.filter(m => m.latest);
  const hotMovies = movies.filter(m => m.hot);

  return (
    <>
      <PageMeta
        title="神马影院-高清电影在线观看 | 免费高清电视剧全集不缓冲秒播"
        description="神马影院提供海量高清电影和电视剧在线观看，无需下载即点即播。涵盖热门高清视频资源，免费高清播放流畅不卡顿，追剧观影首选平台。"
        canonical="https://www.shenmayingyuan.com/"
        schemaScripts={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "神马影院-高清电影在线观看",
            "description": "神马影院提供海量高清电影和电视剧在线观看，无需下载即点即播。",
            "url": "https://www.shenmayingyuan.com/",
            "isPartOf": {
              "@type": "WebSite",
              "name": "神马影院",
              "url": "https://www.shenmayingyuan.com"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          }
        ]}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20interior%20with%20red%20velvet%20curtains%20and%20golden%20stage%20lights%2C%20dramatic%20atmospheric%20lighting%2C%20grand%20elegant%20design%2C%20warm%20colors%2C%20cinematic%20composition%2C%20no%20people&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
            alt="神马影院"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              高清电影，<span className="text-primary-400">即点即播</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
              神马影院汇集海量高清电影和电视剧资源，无需下载，在线观看。高清播放流畅不卡顿，给您极致的观影体验。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/category/all"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                  <i className="ri-play-circle-line"></i>
                </span>
                立即观影
              </Link>
              <Link
                to="/about"
                className="border border-white/30 hover:border-white/60 text-white px-8 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选，不容错过的佳作</p>
              </div>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {featuredMovies.map(movie => {
                const cat = categories.find(c => c.slug === movie.category);
                return (
                  <MovieCardFeatured
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    description={movie.description}
                    rating={movie.rating}
                    poster={movie.poster}
                    year={movie.year}
                    categoryName={cat?.name}
                  />
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  影片分类
                </h2>
                <p className="text-sm text-foreground-500 mt-1">按类型快速找到你想看的</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
                查看全部
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.filter(c => c.slug !== 'all').map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 md:p-5 rounded-lg bg-background-50 hover:bg-accent-50 transition-colors duration-200 cursor-pointer group border border-background-200/70"
                >
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-200">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-accent-700 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚出炉的热门好片，抢先看</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
                更多新片
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latestMovies.slice(0, 6).map(movie => {
                const cat = categories.find(c => c.slug === movie.category);
                return (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    year={movie.year}
                    category={movie.category}
                    rating={movie.rating}
                    poster={movie.poster}
                    categoryName={cat?.name}
                  />
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  热门排行
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的火爆影片</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotMovies.slice(0, 6).map((movie, index) => {
                const cat = categories.find(c => c.slug === movie.category);
                return (
                  <div key={movie.id} className="relative">
                    <div className="absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                      {index + 1}
                    </div>
                    <MovieCard
                      id={movie.id}
                      title={movie.title}
                      year={movie.year}
                      category={movie.category}
                      rating={movie.rating}
                      poster={movie.poster}
                      categoryName={cat?.name}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">关于神马影院的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 cursor-pointer">
                    {faq.question}
                    <span className="w-5 h-5 flex items-center justify-center transition-transform duration-200 group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-900">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-3">
              准备好开始观影了吗？
            </h2>
            <p className="text-sm text-foreground-400 mb-8 leading-relaxed">
              加入神马影院，畅享海量高清电影电视剧。无需下载，即点即播，让观影变得简单纯粹。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
              开始免费观影
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}